<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import authService from '../services/auth.service'

const route = useRoute()
const router = useRouter()
const { t, locale } = useI18n()

const loading = ref(true)
const success = ref(false)
const error = ref<string | null>(null)
const message = ref<string>('')

// Activate account on component mount
onMounted(async () => {
  const email = route.query.email as string
  const token = route.query.token as string
  const lang = route.query.lang as string

  // Set language from URL parameter if provided
  if (lang && ['pl', 'en', 'fr', 'es', 'de', 'it', 'pt', 'cs', 'nl', 'sv', 'da', 'hu', 'ro', 'no', 'lt', 'hr', 'fi'].includes(lang)) {
    locale.value = lang
  }

  if (!email || !token) {
    error.value = t('activate.invalidLink')
    loading.value = false
    return
  }

  try {
    const response = await authService.activate(token, email)
    success.value = true
    message.value = t('activate.defaultSuccess')

    // Redirect to login after 3 seconds
    setTimeout(() => {
      router.push('/login')
    }, 3000)
  } catch (err: any) {
    error.value = err.response?.data?.message || t('activate.defaultError')
  } finally {
    loading.value = false
  }
})
</script>

<template>
  <div class="min-h-screen bg-gray-50 flex items-center justify-center p-4">
    <div class="w-full max-w-md">
      <!-- Loading State -->
      <div v-if="loading" class="bg-white rounded-lg shadow-lg p-8 text-center">
        <div class="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
        <h2 class="text-xl font-semibold text-gray-900 mb-2">{{ t('activate.activating') }}</h2>
        <p class="text-gray-600">{{ t('activate.pleaseWait') }}</p>
      </div>

      <!-- Success State -->
      <div v-else-if="success" class="bg-white rounded-lg shadow-lg p-8 text-center">
        <div class="mb-4">
          <svg class="w-16 h-16 text-green-600 mx-auto" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
          </svg>
        </div>
        <h2 class="text-2xl font-bold text-gray-900 mb-2">{{ t('activate.success') }}</h2>
        <p class="text-gray-600 mb-6">{{ message }}</p>
        <p class="text-sm text-gray-500">{{ t('activate.redirecting') }}</p>
        <router-link
          to="/login"
          class="mt-4 inline-block px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
        >
          {{ t('activate.goToLogin') }}
        </router-link>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="bg-white rounded-lg shadow-lg p-8 text-center">
        <div class="mb-4">
          <svg class="w-16 h-16 text-red-600 mx-auto" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
          </svg>
        </div>
        <h2 class="text-2xl font-bold text-gray-900 mb-2">{{ t('activate.error') }}</h2>
        <p class="text-red-600 mb-6">{{ error }}</p>
        <div class="space-y-2">
          <router-link
            to="/login"
            class="block px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          >
            {{ t('activate.goToLogin') }}
          </router-link>
          <router-link
            to="/register"
            class="block px-6 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
          >
            {{ t('activate.registerAgain') }}
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>
